import React from 'react';

import { getIn } from 'formik';
import PropTypes from 'prop-types';

import { ComboBox } from 'components/FormHelper/ComboBox/ComboBox';
import { FormDateField } from 'components/FormHelper/FormDateField';
import { InputField } from 'components/FormHelper/InputField';
import { HintText } from 'components/HintText/HintText';
import { HINTTEXT_TYPE } from 'components/HintText/HintTextType';
import { LabelRadiobutton } from 'components/LabelRadiobutton/LabelRadiobutton';

import { getFieldLabel, getFieldType, getRadioOptions, isDateField } from 'utils/FieldNameMapping';

const getResolvableFieldName = (name) => {
  if (!name) {
    return name;
  }

  const dotIndex = name.lastIndexOf('.');
  if (dotIndex !== -1) {
    return name.slice(dotIndex + 1);
  }

  return name;
};

const resolveFieldType = (name, typeOverride) => {
  if (typeOverride) {
    return typeOverride;
  }

  const baseName = getResolvableFieldName(name);
  const mappedType = getFieldType(baseName);

  if (mappedType) {
    return mappedType;
  }

  if (isDateField(baseName)) {
    return 'date';
  }

  return 'text';
};

const resolveLabel = (name, labelOverride) => {
  if (labelOverride) {
    return labelOverride;
  }

  return getFieldLabel(getResolvableFieldName(name)) || getFieldLabel(name) || '';
};

const getRadioChecked = (rawValue, optionValue) => {
  if (typeof optionValue === 'boolean') {
    const boolValue =
      rawValue === true || rawValue === 'true' || rawValue === '1' || rawValue === 1;
    return boolValue === optionValue;
  }

  return Number(rawValue) === optionValue;
};

export const FormField = ({
  name,
  formik,
  label,
  type,
  options,
  items,
  disabled,
  readOnly,
  infoText,
  oldValue,
  fullWidth,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  disablePaste,
  isEdited,
  testId,
  id,
  multiline,
  select,
  className,
  radioClassName,
  radioGroupClassName,
  radioLabelClassName,
}) => {
  const resolvedType = resolveFieldType(name, type);
  const resolvedLabel = resolveLabel(name, label);
  const baseName = getResolvableFieldName(name);
  const isDisabled = disabled || readOnly;
  const shouldDisablePaste = disablePaste ?? baseName === 'confirmEmail';

  const formikError = formik ? getIn(formik.errors, name) : undefined;
  const fieldValue = value ?? (formik ? getIn(formik.values, name) : undefined) ?? '';
  const fieldError = error ?? Boolean(formikError);
  const fieldHelperText = helperText ?? formikError ?? '';
  const fieldOldValue = oldValue ?? (formik ? getIn(formik.initialValues, name) : undefined) ?? '';

  const handleBlur = (event) => {
    if (onBlur) {
      onBlur(event);
      return;
    }
    formik?.handleBlur(event);
  };

  const handleFormikChange = (fieldName, fieldValueOrEvent) => {
    if (onChange) {
      if (typeof fieldValueOrEvent === 'object' && fieldValueOrEvent?.target) {
        onChange(fieldValueOrEvent);
        return;
      }

      onChange(fieldName, fieldValueOrEvent);
      return;
    }

    if (!formik) {
      return;
    }

    if (typeof fieldValueOrEvent === 'object' && fieldValueOrEvent?.target) {
      formik.handleChange(fieldValueOrEvent);
      return;
    }

    formik.setFieldValue(fieldName, fieldValueOrEvent);
  };

  if (resolvedType === 'radio') {
    const radioOptions = options?.length ? options : getRadioOptions(baseName);

    return (
      <div className={radioClassName || className}>
        {resolvedLabel && (
          <div className={radioLabelClassName || 'form-field__radio-label primary-text-color'}>
            {resolvedLabel}
          </div>
        )}
        <div className={radioGroupClassName || 'form-field__radio-group'}>
          {radioOptions.map((option) => (
            <LabelRadiobutton
              key={`${name}-${String(option.value)}`}
              id={id ? `${id}-${String(option.value)}` : `${name}-${String(option.value)}`}
              name={name}
              label={option.label}
              value={option.value}
              checked={getRadioChecked(fieldValue, option.value)}
              disabled={isDisabled}
              error={fieldError}
              onChange={isDisabled ? () => {} : formik?.handleChange || onChange}
            />
          ))}
        </div>
        {fieldError && fieldHelperText ? (
          <HintText type={HINTTEXT_TYPE.ERROR}>{fieldHelperText}</HintText>
        ) : null}
      </div>
    );
  }

  if (resolvedType === 'comboBox') {
    return (
      <ComboBox
        options={options || []}
        name={name}
        label={resolvedLabel}
        value={fieldValue ?? ''}
        oldValue={fieldOldValue}
        error={fieldError}
        helperText={fieldHelperText}
        disabled={isDisabled}
        isEdited={isEdited}
        onChange={(fieldName, nextValue) => handleFormikChange(fieldName, nextValue)}
        fullWidth={fullWidth}
      />
    );
  }

  if (resolvedType === 'date') {
    if (readOnly) {
      return (
        <InputField
          fullWidth={fullWidth}
          label={resolvedLabel}
          name={name}
          value={fieldValue ?? ''}
          oldValue={fieldOldValue}
          disabled
          error={fieldError}
          helperText={fieldHelperText}
          infoText={infoText}
          isEdited={isEdited}
          testId={testId}
        />
      );
    }

    return (
      <FormDateField
        name={name}
        label={resolvedLabel}
        value={fieldValue ?? ''}
        onChange={handleFormikChange}
        error={fieldError}
        helperText={fieldHelperText}
        disabled={disabled}
        readOnly={readOnly}
        infoText={infoText}
        oldValue={fieldOldValue}
        fullWidth={fullWidth}
        isEdited={isEdited}
        testId={testId}
        onBlur={handleBlur}
      />
    );
  }

  const selectItems = items || options || [];

  return (
    <InputField
      fullWidth={fullWidth}
      label={resolvedLabel}
      name={name}
      id={id}
      oldValue={fieldOldValue}
      variant="standard"
      disabled={isDisabled}
      value={fieldValue ?? ''}
      onChange={
        isDisabled
          ? undefined
          : (event) => {
              if (onChange) {
                onChange(event);
                return;
              }
              formik.handleChange(event);
            }
      }
      onBlur={isDisabled ? undefined : handleBlur}
      error={fieldError}
      helperText={fieldHelperText}
      infoText={infoText}
      select={select || resolvedType === 'select'}
      items={selectItems}
      disablePaste={shouldDisablePaste}
      isEdited={isEdited}
      testId={testId}
      multiline={multiline}
    />
  );
};

FormField.propTypes = {
  name: PropTypes.string.isRequired,
  formik: PropTypes.shape({
    values: PropTypes.object,
    errors: PropTypes.object,
    initialValues: PropTypes.object,
    handleChange: PropTypes.func,
    setFieldValue: PropTypes.func,
  }),
  label: PropTypes.string,
  type: PropTypes.oneOf(['text', 'date', 'comboBox', 'select', 'radio', 'checkbox']),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
      title: PropTypes.string,
    })
  ),
  items: PropTypes.arrayOf(PropTypes.object),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  infoText: PropTypes.string,
  oldValue: PropTypes.string,
  fullWidth: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disablePaste: PropTypes.bool,
  isEdited: PropTypes.bool,
  testId: PropTypes.string,
  id: PropTypes.string,
  multiline: PropTypes.bool,
  select: PropTypes.bool,
  className: PropTypes.string,
  radioClassName: PropTypes.string,
  radioGroupClassName: PropTypes.string,
  radioLabelClassName: PropTypes.string,
};

FormField.defaultProps = {
  formik: null,
  label: '',
  type: null,
  options: [],
  items: [],
  disabled: false,
  readOnly: false,
  infoText: '',
  oldValue: '',
  fullWidth: true,
  value: undefined,
  onChange: null,
  onBlur: null,
  error: undefined,
  helperText: undefined,
  disablePaste: undefined,
  isEdited: false,
  testId: '',
  id: '',
  multiline: false,
  select: false,
  className: '',
  radioClassName: '',
  radioGroupClassName: '',
  radioLabelClassName: '',
};
